import React, { useCallback, memo } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
  Button,
  Grid,
  TextField,
  Box,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { FileLibrary, SeriesData, LinkData } from "../../context/FileLibraryContext";
import EpisodeEditor from "./EpisodeEditor";

interface SeriesEditorProps {
  seriesList: SeriesData[];
  onChange: (newLib: FileLibrary) => void;
}

const SeriesEditor: React.FC<SeriesEditorProps> = ({ seriesList, onChange }) => {
  const updateSeries = useCallback(
    (idx: number, updated: SeriesData) => {
      const newSeries = [...seriesList];
      newSeries[idx] = updated;
      onChange({ series: newSeries });
    },
    [seriesList, onChange]
  );

  const addSeries = useCallback(() => {
    const newSeries: SeriesData = {
      id: Date.now().toString(),
      name: "",
      seriesTier: 0,
      type: "",
      cover: "",
      flavorText: "",
      description: "",
      intensity: "❤️",
      tags: [],
      cw: [],
      seriesLinks: [],
      episodes: [],
    };
    onChange({ series: [...seriesList, newSeries] });
  }, [seriesList, onChange]);

  const deleteSeries = useCallback(
    (idx: number) => {
      const filtered = seriesList.filter((_, i) => i !== idx);
      onChange({ series: filtered });
    },
    [seriesList, onChange]
  );

  const moveSeries = useCallback(
    (from: number, to: number) => {
      const list = [...seriesList];
      const [item] = list.splice(from, 1);
      list.splice(to, 0, item);
      onChange({ series: list });
    },
    [seriesList, onChange]
  );

  const handleFieldChange = useCallback(
    <K extends keyof SeriesData>(idx: number, field: K, value: SeriesData[K]) => {
      const s = { ...seriesList[idx], [field]: value };
      updateSeries(idx, s);
    },
    [seriesList, updateSeries]
  );

  const handleListChange = useCallback(
    (idx: number, field: 'tags' | 'cw' | 'seriesLinks', list: any[]) => {
      const s = { ...seriesList[idx], [field]: list } as SeriesData;
      updateSeries(idx, s);
    },
    [seriesList, updateSeries]
  );

  return (
    <>
      {seriesList.map((series, sIdx) => (
        <Accordion key={series.id} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ flexGrow: 1 }}>
              {series.name || `Series ${sIdx + 1}`}
            </Typography>
            <IconButton size="small" disabled={sIdx === 0} onClick={e => { e.stopPropagation(); moveSeries(sIdx, sIdx - 1); }}>
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" disabled={sIdx === seriesList.length - 1} onClick={e => { e.stopPropagation(); moveSeries(sIdx, sIdx + 1); }}>
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={e => { e.stopPropagation(); deleteSeries(sIdx); }}>
              <DeleteIcon />
            </IconButton>
          </AccordionSummary>

          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Name"
                  fullWidth
                  value={series.name}
                  onChange={e => handleFieldChange(sIdx, 'name', e.target.value)}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  label="Access Tier"
                  type="number"
                  fullWidth
                  value={series.seriesTier}
                  onChange={e => handleFieldChange(sIdx, 'seriesTier', Number(e.target.value))}
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <TextField
                  label="Cover URL"
                  fullWidth
                  value={series.cover}
                  onChange={e => handleFieldChange(sIdx, 'cover', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Flavor Text"
                  fullWidth
                  multiline
                  value={series.flavorText}
                  onChange={e => handleFieldChange(sIdx, 'flavorText', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  value={series.description}
                  onChange={e => handleFieldChange(sIdx, 'description', e.target.value)}
                />
              </Grid>

              {/* Tags */}
              <Grid item xs={12} md={6}>
                <Typography>Tags</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  {series.tags.map(tag => (
                    <Chip key={tag} label={tag} onDelete={() => handleListChange(sIdx, 'tags', series.tags.filter(t => t !== tag))} />
                  ))}
                </Box>
                <Button size="small" startIcon={<AddIcon />} onClick={() => handleListChange(sIdx, 'tags', [...series.tags, ''])}>
                  Add Tag
                </Button>
              </Grid>

              {/* Content Warnings */}
              <Grid item xs={12} md={6}>
                <Typography>Content Warnings</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  {series.cw.map(cw => (
                    <Chip key={cw} label={cw} onDelete={() => handleListChange(sIdx, 'cw', series.cw.filter(c => c !== cw))} />
                  ))}
                </Box>
                <Button size="small" startIcon={<AddIcon />} onClick={() => handleListChange(sIdx, 'cw', [...series.cw, ''])}>
                  Add CW
                </Button>
              </Grid>

            </Grid>

            {/* Series Links Editor */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12}>
                <Typography>Series .zip Download Links</Typography>
                {series.seriesLinks.map((link, lIdx) => (
                    <Box sx={{ display: 'flex', flexDirection:'row', alignItems: 'center', mb: 1, mt: 1 }} key={lIdx}>
                        <TextField
                            fullWidth
                            value={link.title}
                            sx={{flexShrink: 4 }}
                            onChange={e => {
                                const newLinks = [...series.seriesLinks];
                                newLinks[lIdx] = { ...newLinks[lIdx], title: e.target.value };
                                handleListChange(sIdx, 'seriesLinks', newLinks);
                            }}
                        />
                        <TextField
                          key={lIdx}
                          fullWidth
                          value={link.url}
                          sx={{ flexGrow: 6, p:0.5 }}
                          onChange={e => {
                            const newLinks = [...series.seriesLinks];
                            newLinks[lIdx] = { ...newLinks[lIdx], url: e.target.value };
                            handleListChange(sIdx, 'seriesLinks', newLinks);
                          }}
                        />
                        <Button 

                              size="small"
                              onClick={() => {
                              const newLinks = series.seriesLinks.filter((_, idx) => idx !== lIdx);
                              handleListChange(sIdx, 'seriesLinks', newLinks);
                              }}>
                            <DeleteIcon />
                        </Button>
                    </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const newLinks = [...series.seriesLinks, { url: "", type: "", accessTier: 0, title: "" }];
                    handleListChange(sIdx, 'seriesLinks', newLinks);
                  }}
                >
                  Add Link
                </Button>
              </Grid>
            </Grid>

            {/* Episodes Editor */}
            <EpisodeEditor
              seriesIndex={sIdx}
              episodes={series.episodes}
              onChange={eps => updateSeries(sIdx, { ...series, episodes: eps })}
            />
          </AccordionDetails>
        </Accordion>
      ))}
      
      <Button variant="contained" startIcon={<AddIcon />} onClick={addSeries} sx={{ mb: 2 }}>
        Add Series
      </Button>
    </>
  );
};

export default memo(SeriesEditor);
